import { useState } from "react";
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Backdrop,
  Tooltip,
  Menu,
} from "@mui/material";
import { BsEraserFill } from "react-icons/bs";
import { CiCirclePlus, CiSearch } from "react-icons/ci";
import { OrdersPopUpModal } from "./OrdersPopUpModal";
import { ComboBox } from "./ComboBox";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import InfoIcon from "@mui/icons-material/Info";
// Özel stil komponenti
const StyledPaginationBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px",
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  marginTop: "24px",
  gap: "16px",
  flexWrap: "wrap",

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    padding: "12px",
    gap: "12px",
  },

  ".pagination-controls": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: "#F7F9FC",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    flexWrap: "wrap",
    justifyContent: "center",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "center",
      padding: "8px",
      gap: "4px",
    },
  },

  ".page-size-selector": {
    minWidth: "150px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  ".records-info": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#6B7280",
    fontSize: "0.875rem",
    flexWrap: "wrap",
    justifyContent: "center",

    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      width: "100%",
      fontSize: "0.75rem",
    },

    "& strong": {
      color: "#1F1B45",
      fontWeight: 600,
    },
  },
}));

const GenericTable = ({
  data,
  headers,
  filterFields,
  setFilterFields,
  filterLogic,
  RowComponent,
  filters: filtersDefault,
  comboBoxFilterFields,
  fieldsToClear,
  rangeInputFilterFields,
  setFiltersValue,
  onSearchFilters,
  setComboBoxFilter,
  comboBoxFilter,
  filtersValue,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  totalItems,
  isLoading = false,
  genericFunction,
  setSelectedData,
  selectedData,
  tableButtons = [],
  setSelectedKdv,
  selectedKdv,
  bulkActionMenu,
}) => {
  const [filters, setFilters] = useState(filtersDefault);
  const [activeFilters, setActiveFilters] = useState(filtersDefault);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);

  const handleFilterChange = (event) => {
    if (event) {
      const { name, value } = event.target;
      setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
      setComboBoxFilter((prevFilters) => ({ ...prevFilters, [name]: value }));

      setFilterFields((prevFields) =>
        prevFields.map((field) =>
          field.name === name ? { ...field, value } : field
        )
      );
    }
  };

  const clearFilters = async () => {
    try {
      // ComboBox filtrelerini temizle
      setComboBoxFilter({});

      // Tüm state'leri sıfırla
      setFilters({});
      setActiveFilters({});
      setFiltersValue({});

      // FilterFields'ı sıfırla
      setFilterFields((prevFields) =>
        prevFields.map((field) => ({ ...field, value: "" }))
      );

      // Seçili sayfayı sıfırla
      setCurrentPage(1);

      // Tüm filtreleri boş göndererek yeni veriyi getir
      await onSearchFilters({});
    } catch (error) {
      console.error("Veri getirme hatası:", error);
    }
  };

  const handleViewMoreClick = (item) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleAutoboxFilterChange = (inputName, newValue) => {
    const value = newValue?.value || null;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [inputName]: value,
    }));

    setComboBoxFilter((prevFilters) => ({
      ...prevFilters,
      [inputName]: value,
    }));
  };

  const handleOnSearchFilters = async () => {
    try {
      const currentFilters = comboBoxFilter || filters;
      setActiveFilters(currentFilters);
      setFiltersValue(currentFilters);
      await setCurrentPage(1);
      await onSearchFilters(currentFilters);
    } catch (error) {
      console.error("Arama hatası:", error);
    }
  };

  const filteredData = data?.filter((item) => filterLogic(item, activeFilters));

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const handlePageClick = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  const isItemSelectable = (item) => {
    return item?.invoiced != true;
  };

  const isItemSelected = (item) => {
    return selectedData?.some((selected) => selected.id === item.id);
  };

  const selectableItems = data?.filter((item) => isItemSelectable(item)) || [];
  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => isItemSelected(item));
  const someSelected =
    selectableItems.some((item) => isItemSelected(item)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      const newSelectedData = selectedData.filter(
        (selected) => !selectableItems.some((item) => item.id === selected.id)
      );
      setSelectedData(newSelectedData);
    } else {
      const newSelectedData = [...selectedData];

      selectableItems.forEach((item) => {
        if (!isItemSelected(item)) {
          newSelectedData.push(item);
        }
      });

      setSelectedData(newSelectedData);
    }
  };

  const handleSelectItem = (item) => {
    if (!isItemSelectable(item)) {
      return;
    }

    if (isItemSelected(item)) {
      setSelectedData(
        selectedData.filter((selected) => selected.id !== item.id)
      );
    } else {
      setSelectedData([...selectedData, item]);
    }
  };
  return (
    <Box
      sx={{
        marginTop: 4,
        width: "100%",
        paddingX: { xs: "4px", sm: "6px" },
        position: "relative",
      }}
    >
      {/* Loading Overlay */}
      <Backdrop
        sx={{
          position: "absolute",
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
        }}
        open={isLoading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress color="primary" />
          <Typography
            variant="body2"
            sx={{
              color: "#1F1B45",
              fontWeight: 500,
              backgroundColor: "white",
              padding: "8px 16px",
              borderRadius: "10px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            Veriler Yükleniyor...
          </Typography>
        </Box>
      </Backdrop>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 2, sm: 3 },
          marginBottom: { xs: 2, sm: 4 },
          borderRadius: 3,
          border: "1px solid #E3F2FD",
          backgroundColor: "#FFFFFF",
          opacity: isLoading ? 0.7 : 1,
          transition: "opacity 0.2s ease",
          boxShadow: "0 2px 8px rgba(13, 71, 161, 0.08)",
        }}
      >
        <Box sx={{ marginBottom: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#1F1B45",
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            Filtreler
          </Typography>
        </Box>
        <Grid
          container
          spacing={{ xs: 1, sm: 2, md: 2, lg: 3 }}
          sx={{
            "& .MuiGrid-item": {
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {filterFields.map((field) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={field.name}
              sx={{
                "& .MuiTextField-root": {
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: { xs: "36px", sm: "40px", md: "44px" },
                    fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" },
                  },
                },
              }}
            >
              {field.type === "dateRange" ? (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: { xs: "6px", sm: "8px", md: "10px" },
                      border: "1px solid #BBDEFB",
                      borderRadius: "10px",
                      height: { xs: "36px", sm: "40px", md: "44px" },
                      "&:hover": {
                        borderColor: "#1565C0",
                      },
                      "& input": {
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.875rem",
                          md: "0.875rem",
                        },
                      },
                    }}
                  >
                    <input
                      type="date"
                      value={filters[field.startName] || ""}
                      onChange={(e) =>
                        handleFilterChange({
                          target: {
                            name: field.startName,
                            value: e.target.value,
                          },
                        })
                      }
                      style={{
                        border: "none",
                        outline: "none",
                        width: "45%",
                        padding: "0",
                      }}
                    />
                    <Typography
                      sx={{
                        color: "#666",
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.875rem",
                          md: "0.875rem",
                        },
                      }}
                    >
                      -
                    </Typography>
                    <input
                      type="date"
                      value={(filters[field.endName] || "").split("T")[0]}
                      onChange={(e) =>
                        handleFilterChange({
                          target: {
                            name: field.endName,
                            value: e.target.value + "T23:59",
                          },
                        })
                      }
                      style={{
                        border: "none",
                        outline: "none",
                        width: "45%",
                        padding: "0",
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <TextField
                  size="small"
                  label={field.label}
                  type={field.type}
                  name={field.name}
                  value={field.value || ""}
                  onChange={handleFilterChange}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1565C0",
                        },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      transform: "translate(14px, -9px) scale(0.75)",
                      '&[data-shrink="false"]': {
                        transform: "translate(14px, 8px) scale(1)",
                      },
                      color: "#666",
                    },
                  }}
                />
              )}
            </Grid>
          ))}

          {comboBoxFilterFields.map((selectField) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={selectField.name}
              sx={{
                "& .MuiFormControl-root": {
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: { xs: "36px", sm: "40px", md: "44px" },
                    fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" },
                  },
                },
              }}
            >
              <ComboBox
                handleAutoboxFilterChange={handleAutoboxFilterChange}
                handleFilterChange={handleFilterChange}
                selectField={selectField}
                filters={comboBoxFilter != null ? comboBoxFilter : filters}
              />
            </Grid>
          ))}

          {rangeInputFilterFields.map((rangeField) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={rangeField.name}
              sx={{
                "& .MuiTextField-root": {
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: { xs: "36px", sm: "40px", md: "44px" },
                    fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" },
                  },
                },
              }}
            >
              <TextField
                size="small"
                label={rangeField.label}
                type="number"
                name={rangeField.name}
                value={filters[rangeField.name] || ""}
                onChange={handleFilterChange}
                fullWidth
                variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1565C0",
                        },
                      },
                    },
                  "& .MuiInputLabel-root": {
                    transform: "translate(14px, -9px) scale(0.75)",
                    '&[data-shrink="false"]': {
                      transform: "translate(14px, 8px) scale(1)",
                    },
                    color: "#666",
                  },
                }}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 2, md: 3 },
                mt: { xs: 2, sm: 2, md: 3 },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 2, sm: 2, md: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<BsEraserFill />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    color: "#666",
                    borderColor: "#BBDEFB",
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: "#E3F2FD",
                      borderColor: "#1565C0",
                    },
                    padding: {
                      xs: "6px 16px",
                      sm: "8px 20px",
                      md: "10px 24px",
                    },
                    fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
                    height: { xs: "36px", sm: "40px", md: "44px" },
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "auto", sm: "140px", md: "160px" },
                  }}
                >
                  Filtreleri Temizle
                </Button>
                <Button
                  variant="contained"
                  onClick={handleOnSearchFilters}
                  startIcon={<CiSearch />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    backgroundColor: "#1565C0",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: "#0D47A1",
                    },
                    padding: {
                      xs: "6px 16px",
                      sm: "8px 20px",
                      md: "10px 24px",
                    },
                    fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
                    height: { xs: "36px", sm: "40px", md: "44px" },
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "auto", sm: "120px", md: "140px" },
                  }}
                >
                  Ara
                </Button>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 2, sm: 2, md: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  width: { xs: "100%", sm: "auto" },
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                {genericFunction && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: { xs: 2, sm: 2, md: 3 },
                      width: { xs: "100%", sm: "auto" },
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: "180px", md: "220px" },
                        mb: { xs: 1, sm: 0 },
                        flexWrap: "wrap",
                      }}
                    >
                      <FormControl
                        sx={{
                          width: { xs: "100%", sm: 140, md: 160 },
                          "& .MuiOutlinedInput-root": {
                            height: { xs: "36px", sm: "40px", md: "44px" },
                            borderRadius: "10px",
                            fontSize: {
                              xs: "0.875rem",
                              sm: "0.875rem",
                              md: "1rem",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.875rem",
                              md: "0.875rem",
                            },
                          },
                        }}
                        size="small"
                      >
                        <InputLabel>KDV Oranı</InputLabel>
                        <Select
                          value={selectedKdv}
                          onChange={(e) => setSelectedKdv(e.target.value)}
                          label="KDV Oranı"
                        >
                          {[0, 1, 8, 10, 18, 20].map((value) => (
                            <MenuItem
                              key={value}
                              value={value}
                              sx={{
                                fontSize: {
                                  xs: "0.875rem",
                                  sm: "0.875rem",
                                  md: "1rem",
                                },
                              }}
                            >
                              %{value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip
                        title="Seçilen KDV oranı, listeye eklenecek tüm siparişlere uygulanacaktır. Seçim yapılmazsa mevcut KDV oranları korunacaktır."
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{
                            ml: 1,
                            "& .MuiSvgIcon-root": {
                              fontSize: {
                                xs: "1.25rem",
                                sm: "1.25rem",
                                md: "1.5rem",
                              },
                            },
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={genericFunction}
                      startIcon={<CiCirclePlus />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 500,
                        backgroundColor: "#1565C0",
                        "&:hover": {
                          backgroundColor: "#0D47A1",
                        },
                        padding: {
                          xs: "6px 16px",
                          sm: "8px 20px",
                          md: "10px 24px",
                        },
                        fontSize: {
                          xs: "0.875rem",
                          sm: "0.875rem",
                          md: "1rem",
                        },
                        height: { xs: "36px", sm: "40px", md: "44px" },
                        minWidth: { xs: "auto", sm: "180px", md: "200px" },
                        width: { xs: "100%", sm: "auto" },
                        boxShadow: 2,
                        borderRadius: "10px",
                      }}
                    >
                      Fatura Listesine Ekle
                    </Button>
                    {tableButtons}
                  </Box>
                )}
                {!genericFunction && tableButtons.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 2, sm: 2, md: 3 },
                      flexDirection: { xs: "column", sm: "row" },
                      width: { xs: "100%", sm: "auto" },
                      alignItems: { xs: "stretch", sm: "center" },
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                    }}
                  >
                    {tableButtons}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 2, sm: 4 },
          overflow: "auto",
          backgroundColor: "#FFFFFF",
          opacity: isLoading ? 0.7 : 1,
          transition: "opacity 0.2s ease",
          border: "1px solid #E3F2FD",
          boxShadow: "0 2px 8px rgba(13, 71, 161, 0.08)",
        }}
      >
        <TableContainer
          sx={{
            maxWidth: "100%",
            overflowX: "auto",
            "& .MuiTableCell-root": {
              whiteSpace: "nowrap",
              minWidth: { xs: "120px", sm: "150px" },
            },
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "#E3F2FD" }}>
              <TableRow>
                <TableCell sx={{ width: { xs: "40px", sm: "50px" }, position: "relative" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                      sx={{
                        "&.Mui-checked": { color: "#1F1B45" },
                        "&.Mui-indeterminate": { color: "#1F1B45" },
                      }}
                    />
                    {selectedData && selectedData.length > 0 && bulkActionMenu && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
                          sx={{
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "#E3F2FD",
                            },
                          }}
                        >
                          <MoreVertIcon fontSize="small" sx={{ color: "#1565C0" }} />
                        </IconButton>
                        <Menu
                          anchorEl={bulkMenuAnchor}
                          open={Boolean(bulkMenuAnchor)}
                          onClose={() => setBulkMenuAnchor(null)}
                          PaperProps={{
                            sx: {
                              borderRadius: 2,
                              boxShadow: "0 8px 32px rgba(13, 71, 161, 0.15)",
                              minWidth: 180,
                              border: "1px solid #E3F2FD",
                              marginTop: "8px",
                            },
                          }}
                        >
                          {bulkActionMenu}
                        </Menu>
                      </>
                    )}
                  </Box>
                </TableCell>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData?.map((item) => (
                <RowComponent
                  key={item.id}
                  item={item}
                  onClick={() => {
                    setSelectedItem(item);
                    setOpenDialog(true);
                  }}
                  handleViewMoreClick={handleViewMoreClick}
                  itemToRender={item}
                  handleSelect={() => handleSelectItem(item)}
                  isSelected={isItemSelected(item)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <StyledPaginationBox
        sx={{
          opacity: isLoading ? 0.7 : 1,
          transition: "opacity 0.2s ease",
          pointerEvents: isLoading ? "none" : "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
              },
              "& .MuiInputLabel-root": {
                fontSize: { xs: "0.875rem", sm: "1rem" },
              },
              "& .MuiSelect-select": {
                fontSize: { xs: "0.875rem", sm: "1rem" },
              },
            }}
          >
            <InputLabel>Gösterilecek Kayıt</InputLabel>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              label="Gösterilecek Kayıt"
              sx={{
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                },
              }}
            >
              {[5, 10, 25, 50].map((size) => (
                <MenuItem
                  key={size}
                  value={size}
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  <span>{size} kayıt</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              color: "#666",
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            Seçilen kayıt sayısı: {pageSize}
          </Typography>
        </Box>

        <Box
          className="pagination-controls"
          sx={{
            "& .MuiIconButton-root": {
              padding: { xs: "4px", sm: "8px" },
            },
            "& .MuiTypography-root": {
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
          }}
        >
          <IconButton
            onClick={() => handlePageClick(1)}
            disabled={currentPage === 1}
            size="small"
            sx={{
              color: currentPage === 1 ? "#D1D5DB" : "#1F1B45",
              "&:hover:not(:disabled)": {
                backgroundColor: "rgba(31, 27, 69, 0.08)",
              },
            }}
          >
            <FirstPageIcon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={() => handlePageClick(currentPage - 1)}
            disabled={currentPage === 1}
            size="small"
            sx={{
              color: currentPage === 1 ? "#D1D5DB" : "#1F1B45",
              "&:hover:not(:disabled)": {
                backgroundColor: "rgba(31, 27, 69, 0.08)",
              },
            }}
          >
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              px: { xs: 1, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              borderRadius: "10px",
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              minWidth: { xs: "80px", sm: "100px" },
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: "#1F1B45",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              {currentPage} / {totalPages}
            </Typography>
          </Box>

          <IconButton
            onClick={() => handlePageClick(currentPage + 1)}
            disabled={currentPage === totalPages}
            size="small"
            sx={{
              color: currentPage === totalPages ? "#D1D5DB" : "#1F1B45",
              "&:hover:not(:disabled)": {
                backgroundColor: "rgba(31, 27, 69, 0.08)",
              },
            }}
          >
            <NavigateNextIcon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={() => handlePageClick(totalPages)}
            disabled={currentPage === totalPages}
            size="small"
            sx={{
              color: currentPage === totalPages ? "#D1D5DB" : "#1F1B45",
              "&:hover:not(:disabled)": {
                backgroundColor: "rgba(31, 27, 69, 0.08)",
              },
            }}
          >
            <LastPageIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          className="records-info"
          sx={{
            width: { xs: "100%", sm: "auto" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            Toplam <strong>{totalItems}</strong> kayıttan{" "}
            <strong>
              {Math.min((currentPage - 1) * pageSize + 1, totalItems)}
            </strong>{" "}
            - <strong>{Math.min(currentPage * pageSize, totalItems)}</strong>{" "}
            arası gösteriliyor
          </Typography>
        </Box>
      </StyledPaginationBox>

      <OrdersPopUpModal
        openDialog={openDialog}
        handleCloseDialog={() => setOpenDialog(false)}
        selectedItem={selectedItem}
      />
    </Box>
  );
};

GenericTable.propTypes = {
  data: PropTypes.array.isRequired,
  headers: PropTypes.array.isRequired,
  filterFields: PropTypes.array.isRequired,
  setFilterFields: PropTypes.func.isRequired,
  filterLogic: PropTypes.func.isRequired,
  RowComponent: PropTypes.elementType.isRequired,
  filters: PropTypes.object.isRequired,
  comboBoxFilterFields: PropTypes.array.isRequired,
  fieldsToClear: PropTypes.array.isRequired,
  rangeInputFilterFields: PropTypes.array.isRequired,
  setFiltersValue: PropTypes.func.isRequired,
  onSearchFilters: PropTypes.func.isRequired,
  setComboBoxFilter: PropTypes.func.isRequired,
  comboBoxFilter: PropTypes.object,
  filtersValue: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  totalItems: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  genericFunction: PropTypes.func,
  setSelectedData: PropTypes.func.isRequired,
  selectedData: PropTypes.array.isRequired,
  tableButtons: PropTypes.arrayOf(PropTypes.node),
  setSelectedKdv: PropTypes.func,
  selectedKdv: PropTypes.number,
  bulkActionMenu: PropTypes.node,
};

GenericTable.defaultProps = {
  tableButtons: [],
  setSelectedKdv: () => {},
  selectedKdv: 0,
};

export default GenericTable;
